import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Heart, Shield, Users, Award, Sparkles, TrendingUp, MapPin, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import { useCountUp } from "@/hooks/useCountUp";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { HeroImage } from "@/components/HeroImage";

const ExperienceHighlights = () => {
  const { ref: yearRef, isVisible: yearVisible } = useScrollAnimation(0.3);
  const { ref: clientRef, isVisible: clientVisible } = useScrollAnimation(0.3);
  const yearsCount = useCountUp(38, 2000, yearVisible);
  
  return (
    <div className="mt-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent">
          Trusted Excellence
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Years Card */}
        <Card 
          ref={yearRef}
          className="group relative p-10 text-center overflow-hidden bg-gradient-to-br from-secondary/20 via-card to-card border-2 border-secondary/30 hover:border-secondary/60 hover:shadow-2xl hover:shadow-secondary/30 transition-all duration-500 hover:-translate-y-2 hover:scale-105 animate-float"
        >
          {/* Pulsing glow background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(var(--secondary-rgb),0.15),transparent_70%)] animate-pulse-glow"></div>
          
          {/* Decorative icon badge */}
          <div className="absolute -top-3 -right-3 p-3 bg-gradient-to-br from-secondary to-accent rounded-full shadow-lg animate-bounce-subtle">
            <Award className="h-5 w-5 text-white" />
          </div>
          
          <div className="relative z-10">
            <div className="text-7xl font-black bg-gradient-to-br from-secondary via-accent to-secondary bg-clip-text text-transparent mb-3 animate-shimmer bg-[length:200%_100%]">
              {yearsCount}
            </div>
            <div className="text-foreground font-bold text-lg">Years of Experience</div>
            <div className="mt-2 text-sm text-muted-foreground">Since 1987</div>
          </div>
          
          {/* Corner sparkles */}
          <Sparkles className="absolute bottom-3 left-3 h-4 w-4 text-secondary/40 animate-twinkle" />
        </Card>

        {/* Clients Card */}
        <Card 
          ref={clientRef}
          className="group relative p-10 text-center overflow-hidden bg-gradient-to-br from-accent/20 via-card to-card border-2 border-accent/30 hover:border-accent/60 hover:shadow-2xl hover:shadow-accent/30 transition-all duration-500 hover:-translate-y-2 hover:scale-105 animate-float"
          style={{ animationDelay: "200ms" }}
        >
          {/* Pulsing glow background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(var(--accent-rgb),0.15),transparent_70%)] animate-pulse-glow"></div>
          
          {/* Decorative icon badge */}
          <div className="absolute -top-3 -right-3 p-3 bg-gradient-to-br from-accent to-secondary rounded-full shadow-lg animate-bounce-subtle" style={{ animationDelay: "100ms" }}>
            <Users className="h-5 w-5 text-white" />
          </div>
          
          <div className="relative z-10">
            <div className="text-7xl font-black bg-gradient-to-br from-accent via-primary to-accent bg-clip-text text-transparent mb-3 animate-shimmer bg-[length:200%_100%]">
              100s
            </div>
            <div className="text-foreground font-bold text-lg">Satisfied Clients</div>
            <div className="mt-2 text-sm text-muted-foreground">And counting</div>
          </div>
          
          {/* Corner sparkles */}
          <Sparkles className="absolute bottom-3 right-3 h-4 w-4 text-accent/40 animate-twinkle" style={{ animationDelay: "500ms" }} />
        </Card>

        {/* Local Card */}
        <Card 
          className="group relative p-10 text-center overflow-hidden bg-gradient-to-br from-primary/20 via-card to-card border-2 border-primary/30 hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/30 transition-all duration-500 hover:-translate-y-2 hover:scale-105 animate-float"
          style={{ animationDelay: "400ms" }}
        >
          {/* Pulsing glow background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(var(--primary-rgb),0.15),transparent_70%)] animate-pulse-glow"></div>
          
          {/* Decorative icon badge */}
          <div className="absolute -top-3 -right-3 p-3 bg-gradient-to-br from-primary to-secondary rounded-full shadow-lg animate-bounce-subtle" style={{ animationDelay: "200ms" }}>
            <MapPin className="h-5 w-5 text-white" />
          </div>
          
          <div className="relative z-10">
            <div className="text-6xl font-black bg-gradient-to-br from-primary via-secondary to-primary bg-clip-text text-transparent mb-3 animate-shimmer bg-[length:200%_100%]">
              Local
            </div>
            <div className="text-foreground font-bold text-lg">Community Focused</div>
            <div className="mt-2 text-sm text-muted-foreground">Lenexa & KC Metro</div>
          </div>
          
          {/* Corner sparkles */}
          <Sparkles className="absolute top-3 left-3 h-4 w-4 text-primary/40 animate-twinkle" style={{ animationDelay: "1000ms" }} />
        </Card>
      </div>
    </div>
  );
};

const About = () => {
  const promises = [
    { icon: Users, text: "Personally prepared by me" },
    { icon: Heart, text: "No confusing jargon" },
    { icon: Shield, text: "Transparent pricing" },
    { icon: Award, text: "Fast, secure e-filing" },
  ];

  return (
    <Layout>
      {/* Evolution Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/hero-about-evolution.jpg"
            alt="Evolution of Kansas City from corn fields to modern town over 70 years"
            priority={true}
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        
        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold">70 Years of Kansas City History</h1>
            <p className="text-xl text-primary-foreground/90">
              A Lifetime of Growing Together as a Community
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
              {/* Image placeholder */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="overflow-hidden aspect-square bg-gradient-to-br from-secondary/20 to-accent/20">
                  <img 
                    src="/images/hero-brand-image.png" 
                    alt="Diane - The Tiny Tax Lady" 
                    className="w-full h-full object-cover"
                  />
                </Card>
                
                {/* PTI Directory Badge */}
                <a 
                  href="https://www.ptindirectory.com/tax-firms/kansas/lenexa-ks/212856/dts/diane-l-hardesty"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mx-auto max-w-[70%]"
                >
                  <Card className="p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer border-2 border-border hover:border-primary/50 bg-gradient-to-br from-primary/5 to-secondary/5">
                    <div className="flex items-center justify-center gap-3">
                      <Award className="w-6 h-6 text-primary" />
                      <div className="text-center">
                        <p className="text-sm font-semibold text-foreground">Listed on PTI Directory</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          View Profile <ExternalLink className="w-3 h-3" />
                        </p>
                      </div>
                    </div>
                  </Card>
                </a>
              </div>

              {/* Story */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Hi, I'm Diane</h2>
                  <p className="text-xl md:text-2xl leading-relaxed text-foreground font-medium">
                    For <span className="text-secondary font-bold">nearly four decades</span>, I've been helping Kansas families and small business owners take the stress out of tax season. 
                    <span className="block mt-3 text-lg text-muted-foreground">
                      My goal: make sure you understand your taxes, get every deduction, and feel confident when you file.
                    </span>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">My Story</h3>
                  <p className="text-lg leading-relaxed text-foreground/90">
                    <span className="text-secondary font-semibold">My family has called the Kansas City area home for nearly 70 years</span>, and I've spent nearly four decades of that time helping neighbors and friends with their taxes. What started as a favor for friends became a <span className="text-primary font-semibold">lifelong passion</span>. I've worked with hundreds of clients across Kansas City, specializing in individual and small-business returns. I believe tax preparation should be <span className="font-semibold text-foreground">personal, straightforward, and stress-free</span> — not intimidating or confusing.
                  </p>
                </div>

                <div className="bg-muted/50 p-6 rounded-lg border-l-4 border-secondary">
                  <p className="text-lg font-medium italic">
                    "I'm here to make tax season simple, so you can focus on what matters most to you."
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">— Diane, The Tiny Tax Lady</p>
                </div>
              </div>
            </div>

            {/* My Promise */}
            <div>
              <div className="text-center mb-12 space-y-3">
                <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-fade-in">
                  My Promise to You
                </h2>
                <div className="flex items-center justify-center gap-2">
                  <div className="h-1 w-12 bg-gradient-to-r from-transparent via-secondary to-transparent rounded-full"></div>
                  <Sparkles className="h-4 w-4 text-secondary animate-pulse" />
                  <div className="h-1 w-12 bg-gradient-to-r from-transparent via-secondary to-transparent rounded-full"></div>
                </div>
                <p className="text-muted-foreground animate-fade-in" style={{ animationDelay: "100ms" }}>
                  Four pillars of excellence that define every client experience
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {promises.map((promise, index) => (
                  <Card 
                    key={index} 
                    className="group relative p-8 overflow-hidden hover:shadow-2xl hover:shadow-secondary/20 transition-all duration-500 hover:-translate-y-2 border-2 hover:border-secondary/50 bg-gradient-to-br from-card via-card/90 to-secondary/5 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Decorative corner accents */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-secondary/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-accent/10 to-transparent rounded-tr-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Shine effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                    
                    <div className="flex items-start gap-5 relative z-10">
                      <div className="flex-shrink-0 p-4 bg-gradient-to-br from-secondary/30 to-accent/20 rounded-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-secondary/30">
                        <promise.icon className="h-8 w-8 text-secondary animate-pulse" />
                      </div>
                      <div>
                        <p className="font-bold text-xl group-hover:text-secondary transition-colors duration-300">{promise.text}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Experience Highlights */}
            <ExperienceHighlights />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-secondary via-secondary to-primary relative overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/cta-ready-to-start.jpg"
            alt="Welcoming professional tax office entrance"
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 via-secondary/40 to-primary/40" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.05),transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold animate-fade-in">
              Let's Get Started
            </h2>
            <p className="text-xl text-white/90 animate-fade-in" style={{ animationDelay: "100ms" }}>
              Ready to make tax season easy? Schedule your appointment today.
            </p>
            <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
              <Button asChild size="lg" variant="secondary" className="text-lg shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-float">
                <Link to="/schedule-appointment">Schedule Your Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
